import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Globe, Phone, Mail, MapPin, FileText, ArrowLeft, Wrench, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function VendorDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  const track = (target) => {
    axios.post(`${API}/vendors/${id}/track`, { event: "contact_click", target }).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/vendors/${id}`).then((r) => setV(r.data)).catch(() => setErr("Vendor not found"));
  }, [id]);

  if (err) return (
    <div className="px-6 sm:px-12 lg:px-24 py-24 text-center">
      <h2 className="font-display font-bold text-2xl text-slate-100">{err}</h2>
      <Link to="/search" className="text-orange-500 mt-4 inline-block">← Back to directory</Link>
    </div>
  );
  if (!v) return <div className="px-6 sm:px-12 lg:px-24 py-24 text-center text-slate-500 font-mono uppercase tracking-wider text-sm">Loading...</div>;

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-12">
      <Link to="/search" data-testid="back-to-directory" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-500 mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-sm">
            <div className="flex items-start gap-6 mb-8">
              <div className="w-24 h-24 bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden rounded-sm flex-shrink-0">
                {v.logo_path ? (
                  <img src={`${API_BASE}/api/files/${v.logo_path}`} alt={v.company_name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-10 h-10 text-slate-600" />
                )}
              </div>
              <div className="flex-1">
                {v.featured && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-orange-600 text-white inline-block mb-2">Featured Vendor</span>
                )}
                <h1 data-testid="vendor-name" className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-slate-50 leading-tight">
                  {v.company_name}
                </h1>
                {v.city && (
                  <div className="flex items-center gap-2 text-slate-400 mt-3 font-mono text-sm uppercase tracking-wider">
                    <MapPin className="w-4 h-4 text-orange-500" /> {v.city}{v.state ? `, ${v.state}` : ""}
                  </div>
                )}
              </div>
            </div>

            {v.description && (
              <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">About</div>
                <p className="text-slate-300 leading-relaxed">{v.description}</p>
              </div>
            )}

            {v.categories?.length > 0 && (
              <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Trade Categories</div>
                <div className="flex flex-wrap gap-2">
                  {v.categories.map((c) => (
                    <span key={c} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 bg-slate-800 text-slate-200 border border-slate-700">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {v.keywords?.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Capabilities & Keywords</div>
                <div className="flex flex-wrap gap-2">
                  {v.keywords.map((k) => (
                    <span key={k} className="text-xs font-mono text-slate-400 px-2 py-1 border border-slate-800">#{k}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">Contact</div>
            <div className="space-y-4">
              {v.contact_name && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Contact</div>
                  <div className="text-slate-100 font-semibold">{v.contact_name}</div>
                </div>
              )}
              {v.owner_email && (
                <a href={`mailto:${v.owner_email}`} onClick={() => track("email")} data-testid="vendor-email" className="flex items-center gap-3 text-slate-200 hover:text-orange-500">
                  <Mail className="w-4 h-4 text-orange-500" />
                  <span className="text-sm break-all">{v.owner_email}</span>
                </a>
              )}
              {v.phone && (
                <a href={`tel:${v.phone}`} onClick={() => track("phone")} data-testid="vendor-phone" className="flex items-center gap-3 text-slate-200 hover:text-orange-500">
                  <Phone className="w-4 h-4 text-orange-500" />
                  <span className="text-sm">{v.phone}</span>
                </a>
              )}
              {v.website && (
                <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} onClick={() => track("website")} target="_blank" rel="noreferrer" data-testid="vendor-website" className="flex items-center gap-3 text-slate-200 hover:text-orange-500">
                  <Globe className="w-4 h-4 text-orange-500" />
                  <span className="text-sm break-all">{v.website}</span>
                </a>
              )}
            </div>
          </div>

          {v.line_card_path && (
            <a
              href={`${API_BASE}/api/files/${v.line_card_path}`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-line-card"
              className="block bg-orange-600 text-white p-6 rounded-sm hover:bg-orange-500 transition-colors"
            >
              <FileText className="w-6 h-6 mb-3" />
              <div className="font-display font-bold uppercase tracking-tight text-lg">Download Line Card</div>
              <div className="text-xs uppercase tracking-wider opacity-80 mt-1">PDF Document →</div>
            </a>
          )}
        </aside>
      </div>
    </div>
  );
}
